import TimeM from '@/gf/modules/Time'
import { DateTime } from 'luxon'
import { FormItem } from '../types'

type SavedLineItem = {
  id: string
  externalId: string | null
  inStock: boolean | null
  availableAt: DateTime | null
  product: { mpn: string | null; altMpn: string | null }
  rfqPart: { id: string; mpn: string } | null
}

// When an upload populates the Edit Quote form, recover each saved line's id,
// rfqPartId, and externalId by MPN match. Without this, every uploaded item
// arrives with id: null and cast_assoc on the server deletes-and-recreates all
// line items — wiping part_request_id and rejected_at. See GEA-3059.
//
// Also keep the saved line's availability. The extractor never reads a lead
// time: it hardcodes every row as inStock: true, availabilityDate: null. Without
// this, the next save silently overwrites the seller's hand-entered lead times
// with "in stock, no date". See GEA-5225.
const matchUploadedLineItems = (uploadedItems: FormItem[], savedItems: SavedLineItem[]) => {
  // Track existing line-item ids already claimed by an earlier uploaded row.
  // Two uploaded rows with the same MPN would otherwise both adopt the same id
  // and corrupt the cast_assoc UPDATE on submit. First claimant wins;
  // subsequent ones fall through to id: null and insert as fresh rows.
  const consumed = new Set<string>()
  return uploadedItems.map((uploaded): FormItem => {
    // Trim — extractor occasionally returns whitespace-padded part numbers.
    // Stay case-sensitive: false positives across genuinely different parts
    // would be worse than missing a case-mismatched match.
    const uploadedPn = uploaded.partNumber?.trim() ?? ''
    const uploadedAlts = (uploaded.altPartNumbers ?? []).map((s) => s.trim())
    const match = savedItems.find((existing) => {
      if (consumed.has(existing.id)) return false
      const existingMpn = existing.product.mpn?.trim() ?? ''
      const existingAltMpn = existing.product.altMpn?.trim() ?? ''
      if (existingMpn && uploadedPn && existingMpn === uploadedPn) return true
      if (existingAltMpn && uploadedPn && existingAltMpn === uploadedPn) return true
      if (existingMpn && uploadedAlts.includes(existingMpn)) return true
      if (existingAltMpn && uploadedAlts.includes(existingAltMpn)) return true
      return false
    })
    if (!match) return { ...uploaded, id: null, rfqPartId: null, rfqPartNumber: null }
    consumed.add(match.id)
    return {
      ...uploaded,
      id: match.id,
      rfqPartId: match.rfqPart?.id ?? null,
      rfqPartNumber: match.rfqPart?.mpn ?? null,
      externalId: match.externalId ?? uploaded.externalId,
      inStock: match.inStock ?? true,
      availabilityDate: match.availableAt ? TimeM.toPayload(match.availableAt) : null,
    }
  })
}

export default matchUploadedLineItems
